'use client';

import { faArrowRightLong } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { Title } from '@/components/atoms';
import { Button } from '@/components/atoms';
import { getStrapiMediaUrl, Project } from '@/services';

type ProjectsSectionProps = {
  projects: Project[];
  title?: string;
  viewMoreLink?: string;
  viewMoreText?: string;
};

const ProjectsSection = ({
  projects,
  title = 'Dự án khác',
  viewMoreLink = '/projects',
  viewMoreText = 'Xem thêm',
}: ProjectsSectionProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showNavigation, setShowNavigation] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);

  const totalProjects = projects.length;

  useEffect(() => {
    setShowNavigation(totalProjects > 1);
  }, [totalProjects]);

  const goToPrevious = () => {
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : totalProjects - 1));
  };

  const goToNext = () => {
    setCurrentIndex(prev => (prev < totalProjects - 1 ? prev + 1 : 0));
  };

  useEffect(() => {
    if (sliderRef.current && totalProjects > 1) {
      const cardWidth = 630; // 600px card + 30px gap
      const scrollPosition = currentIndex * cardWidth;

      sliderRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      });
    }
  }, [currentIndex, totalProjects]);

  // Auto-advance every 5 seconds
  useEffect(() => {
    if (!showNavigation) return;

    const interval = setInterval(() => {
      goToNext();
    }, 5000);

    return () => clearInterval(interval);
  }, [showNavigation, currentIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        goToPrevious();
      } else if (event.key === 'ArrowRight') {
        goToNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (totalProjects === 0) {
    return null;
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Black background Box */}
      <Box
        sx={{
          position: 'absolute',
          top: 150,
          left: 'calc(50% - 50vw)',
          height: 500,
          width: '100vw',
          backgroundColor: '#000000',
          zIndex: 0, // Place behind content
        }}
      />
      <Box
        component='section'
        id={'projects-section'}
        data-component='news-section'
        data-total-articles={totalProjects}
        data-current-index={currentIndex}
        data-show-navigation={showNavigation}
        data-has-data={totalProjects > 0}
        sx={{
          position: 'relative',
          zIndex: 1,
          py: { xs: 6, md: 10 },
          width: '70vw',
        }}
      >
        {/* Section Header */}
        <Box
          data-component='news-section-header'
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: { xs: 4, md: 6 },
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box data-component='news-section-title-container'>
            <Title level={4} underlineHeight={4}>
              {title || 'DỰ ÁN'}
            </Title>
          </Box>

          {/* View More Button */}
          <Button
            variant='text'
            href={viewMoreLink}
            data-component='news-section-view-more-button'
            icon={
              <FontAwesomeIcon
                icon={faArrowRightLong}
                style={{ width: '16px', height: '16px' }}
              />
            }
            iconPosition='end'
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: '16px',
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {viewMoreText}
          </Button>
        </Box>

        {/* News Articles Row */}
        <Box
          ref={sliderRef}
          data-component='news-section-slider'
          data-current-index={currentIndex}
          data-total-articles={totalProjects}
          sx={{
            display: 'flex',
            gap: '30px',
            mb: 6,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          {projects.map((project, index) => (
            <Card
              key={index}
              data-component='news-article-card'
              data-article-index={index}
              data-article-title={project.title}
              sx={{
                position: 'relative',
                minWidth: '600px',
                height: '583px',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 0,
                border: '1px solid rgba(0, 0, 0, 0.1)',
                boxShadow: 'none',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.15)',
                  border: '1px solid #EB722E',
                  '& .news-article-title': {
                    color: '#EB722E',
                  },
                  '& .news-article-content::before': {
                    height: '8px',
                  },
                  '& .transition-line::after': {
                    width: '100%',
                  },
                },
              }}
              component='div'
            >
              {/* Article Image */}
              <CardMedia
                component='img'
                height='320'
                data-component='news-article-image'
                data-image-url={
                  project.image
                    ? getStrapiMediaUrl(project.image.url)
                    : '/default-news-image.jpg'
                }
                image={
                  project.image
                    ? getStrapiMediaUrl(project.image.url)
                    : '/default-news-image.jpg'
                }
                alt={project.title}
                sx={{
                  objectFit: 'cover',
                  height: 350,
                }}
              />

              <Box
                className='transition-line'
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: 8,
                  backgroundColor: 'rgba(0, 0, 0, 0.1)', // Gray color
                  overflow: 'hidden',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 0,
                    height: '100%',
                    backgroundColor: '#EB722E',
                    transition: 'width 0.3s ease-in-out',
                  },
                }}
              />

              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                {/* Title */}
                <Typography
                  variant='h5'
                  component='h3'
                  data-component='news-article-title'
                  className='news-article-title'
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: '24px',
                    lineHeight: 1.21,
                    mb: 2,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minHeight: '58px',
                    color: '#220000',
                    transition: 'color 0.3s ease',
                  }}
                >
                  {project.title}
                </Typography>

                {/* Excerpt */}
                <Typography
                  variant='body2'
                  data-component='news-article-excerpt'
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: 1.25,
                    color: '#555555',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {project.description || ''}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Pagination */}
        {showNavigation && (
          <Box
            data-component='news-section-navigation'
            data-current-index={currentIndex}
            data-total-articles={totalProjects}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography
              variant='body2'
              data-component='news-section-counter'
              sx={{
                fontFamily: 'var(--font-inter), Inter, sans-serif',
                fontWeight: 400,
                fontSize: {
                  xs: '0.75rem',
                  lg: '14px',
                },
                lineHeight: 1.21,
                color: 'primary.main',
                textTransform: 'uppercase',
                minWidth: '38px',
                textAlign: 'center',
              }}
            >
              {currentIndex + 1} - {totalProjects}
            </Typography>

            <Box
              data-component='news-section-progress-bar'
              sx={{
                width: { xs: '120px', sm: '180px', lg: '240px' },
                height: '2px',
                backgroundColor: 'rgba(0, 0, 0, 0.25)',
                position: 'relative',
                borderRadius: '1px',
              }}
            >
              <Box
                data-component='news-section-progress-fill'
                sx={{
                  height: '100%',
                  backgroundColor: 'primary.main',
                  borderRadius: '1px',
                  transition: 'width 0.5s ease-out',
                  width: `${((currentIndex + 1) / totalProjects) * 100}%`,
                }}
              />
            </Box>

            <IconButton
              onClick={goToPrevious}
              data-component='news-section-prev-button'
              aria-label='Previous project'
              sx={{
                width: '24px',
                height: '24px',
                color: 'secondary.main',
                '&:hover': {
                  backgroundColor: 'rgba(255, 94, 19, 0.1)',
                },
              }}
            >
              <ChevronLeft sx={{ fontSize: '24px' }} />
            </IconButton>

            <IconButton
              onClick={goToNext}
              data-component='news-section-next-button'
              aria-label='Next project'
              sx={{
                width: '24px',
                height: '24px',
                color: 'secondary.main',
                '&:hover': {
                  backgroundColor: 'rgba(255, 94, 19, 0.1)',
                },
              }}
            >
              <ChevronRight sx={{ fontSize: '24px' }} />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ProjectsSection;
